import React from 'react';
import debounce from 'lodash.debounce';

import * as A from 'fp-ts/Array';
import { GeocodeResult, useGeocoder } from '../../../contexts/GeocoderContext';
import { Input } from '../../../components/Input';
import { EdgeContainer } from '../../../components/Container';
import { SelectableListItem } from '../../../components/SelectableListItem';
import { useAlert } from '../../../contexts/AlertContext';
import { RecentLocationChooser } from '../RecentLocationChooser';

interface LocationFormProps {
  selectLocation: (location: GeocodeResult) => void;
  teamId: string;
}

export const LocationChooser = ({
  selectLocation,
  teamId,
}: LocationFormProps) => {
  const geocoder = useGeocoder();
  const alert = useAlert();
  const [results, setResults] = React.useState<GeocodeResult[]>([]);
  const [loading, setLoading] = React.useState(false);

  // We might be able to extract this somehome to a custom hook, but it's
  // OK for now
  const onSearch = React.useCallback(
    debounce((query: string) => {
      if (!query) {
        setResults([]);
        return;
      }
      setLoading(true);
      geocoder
        .forwardGeocode(query)
        .then(setResults)
        .then(() => setLoading(false))
        .catch((err) => {
          setLoading(false);
          alert(err.message);
        });
    }, 350),
    [geocoder, alert, setResults, setLoading],
  );

  // Cancel the search if the component unmounts
  React.useEffect(() => {
    return onSearch.cancel;
  }, []);

  const onSelect = (result: GeocodeResult) => {
    setResults([]);
    selectLocation(result);
  };

  return (
    <div className="space-y-6">
      <Input
        label="Search for a place"
        onChange={(e) => onSearch(e.target.value)}
      />
      {loading ? <p>Loading...</p> : null}
      <EdgeContainer>
        <ul>
          {results.map((result) => (
            <SelectableListItem
              key={result.address}
              onClick={() => onSelect(result)}
            >
              {result.address}
            </SelectableListItem>
          ))}
        </ul>
      </EdgeContainer>
      {A.isEmpty(results) ? (
        <RecentLocationChooser
          teamId={teamId}
          selectLocation={({ address, lat, lng }) =>
            selectLocation({ address, coords: [lat, lng] })
          }
        />
      ) : null}
    </div>
  );
};
